import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { User, Camera, Save, Lock, Loader2, Trash2, Upload, Activity, RefreshCw, Globe, Package } from 'lucide-react';
import { formatApiError } from '../../lib/api';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import RelatedLists from '../../components/RelatedLists';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProfileSettingsPage() {
  const { user, checkAuth } = useAuth();
  const { t } = useTranslation();
  const [profile, setProfile] = useState(null);
  const [name, setName] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef();

  // Password state
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [pwdSaving, setPwdSaving] = useState(false);

  // Activity log state
  const [activity, setActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);

  // Assigned assets state
  const [myAssets, setMyAssets] = useState([]);
  const [assetsLoading, setAssetsLoading] = useState(false);

  const authHeader = () => {
    const token = localStorage.getItem('savenes_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const loadProfile = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/auth/profile`, { headers: authHeader() });
      setProfile(r.data);
      setName(r.data.name || '');
      setAvatarUrl(r.data.avatar_url || '');
    } catch { toast.error('Failed to load profile'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadProfile(); loadActivity(); loadMyAssets(); }, []);

  const loadMyAssets = async () => {
    setAssetsLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/assets/mine`, { headers: authHeader() });
      setMyAssets(r.data.assets || []);
    } catch (_err) {
      // Asset module may be disabled for the org — silently empty.
      setMyAssets([]);
    } finally { setAssetsLoading(false); }
  };

  const loadActivity = async () => {
    setActivityLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/auth/activity?limit=100`, { headers: authHeader() });
      setActivity(r.data.logs || []);
    } catch (err) {
      console.error('Failed to load activity:', err);
    } finally {
      setActivityLoading(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    if (file.size > 1024 * 1024) { toast.error('Image must be under 1 MB'); return; }
    setUploading(true);
    const reader = new FileReader();
    reader.onload = async (ev) => {
      // Resize via canvas to 256x256
      const img = new Image();
      img.onload = async () => {
        const canvas = document.createElement('canvas');
        canvas.width = 256; canvas.height = 256;
        const ctx = canvas.getContext('2d');
        const size = Math.min(img.width, img.height);
        const sx = (img.width - size) / 2;
        const sy = (img.height - size) / 2;
        ctx.drawImage(img, sx, sy, size, size, 0, 0, 256, 256);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
        setAvatarUrl(dataUrl);
        setUploading(false);
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/auth/profile`, { name, avatar_url: avatarUrl }, { headers: authHeader() });
      toast.success('Profile updated');
      if (checkAuth) await checkAuth();
      loadProfile();
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setSaving(false); }
  };

  const removeAvatar = () => {
    setAvatarUrl('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (newPwd !== confirmPwd) { toast.error("Passwords don't match"); return; }
    if (newPwd.length < 8) { toast.error('New password must be at least 8 characters'); return; }
    setPwdSaving(true);
    try {
      await axios.post(`${API_URL}/api/auth/profile/change-password`, {
        current_password: currentPwd, new_password: newPwd
      }, { headers: authHeader() });
      toast.success('Password changed successfully');
      setCurrentPwd(''); setNewPwd(''); setConfirmPwd('');
    } catch (error) { toast.error(formatApiError(error)); }
    finally { setPwdSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-[#0E7490] animate-spin" /></div>;

  const initials = (profile?.name || '?').split(' ').map(s => s[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div className="space-y-6 max-w-3xl" data-testid="profile-settings-page">
      <div>
        <h1 className="text-3xl font-semibold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>Profile</h1>
        <p className="text-[#64748B] mt-1">Update your personal information and password.</p>
      </div>

      {/* Avatar + Name */}
      <Card className="careiq-card">
        <CardHeader><CardTitle className="text-lg font-semibold text-[#0A2342] flex items-center gap-2"><User className="w-5 h-5 text-[#0E7490]" />Personal Info</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-6">
            <div className="relative">
              {avatarUrl ? (
                <img src={avatarUrl} alt="avatar" className="w-24 h-24 rounded-full object-cover border-2 border-[#E2E8F0]" data-testid="avatar-preview" />
              ) : (
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#0E7490] to-[#7C3AED] flex items-center justify-center text-white text-2xl font-semibold" data-testid="avatar-placeholder">{initials}</div>
              )}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-white border-2 border-[#0E7490] flex items-center justify-center hover:bg-[#0E7490] hover:text-white transition-colors"
                data-testid="avatar-upload-btn"
                disabled={uploading}
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4 text-[#0E7490] hover:text-white" />}
              </button>
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleAvatarUpload} className="hidden" data-testid="avatar-file-input" />
            </div>
            <div>
              <p className="text-sm text-[#64748B]">Click the camera to upload (max 1 MB).</p>
              {avatarUrl && (
                <button onClick={removeAvatar} className="text-xs text-[#991B1B] hover:underline mt-1 flex items-center gap-1" data-testid="avatar-remove-btn">
                  <Trash2 className="w-3 h-3" /> Remove photo
                </button>
              )}
            </div>
          </div>

          <div>
            <Label htmlFor="name">Full Name</Label>
            <Input id="name" value={name} onChange={e => setName(e.target.value)} data-testid="name-input" />
          </div>
          <div>
            <Label>Email</Label>
            <Input value={profile?.email || ''} disabled className="bg-[#F8FAFC]" />
            <p className="text-[10px] text-[#94A3B8] mt-1">Contact your admin to change your email.</p>
          </div>
          <div className="flex justify-end">
            <Button className="careiq-btn-primary" onClick={saveProfile} disabled={saving} data-testid="save-profile-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}Save Changes
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Language preference */}
      <Card className="careiq-card" data-testid="language-preference-card">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-[#0A2342] flex items-center gap-2">
            <Globe className="w-5 h-5 text-[#0E7490]" />
            {t('preferences.languageSection')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-[#64748B]">{t('preferences.languageDescription')}</p>
          <LanguageSwitcher variant="inline" testId="preferences-language-switcher" />
        </CardContent>
      </Card>

      {/* Change Password */}
      <Card className="careiq-card">
        <CardHeader><CardTitle className="text-lg font-semibold text-[#0A2342] flex items-center gap-2"><Lock className="w-5 h-5 text-[#0E7490]" />Change Password</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={changePassword} className="space-y-3">
            <div><Label>Current Password</Label><Input type="password" value={currentPwd} onChange={e => setCurrentPwd(e.target.value)} required data-testid="current-pwd-input" /></div>
            <div className="grid md:grid-cols-2 gap-3">
              <div><Label>New Password (min 8 chars)</Label><Input type="password" value={newPwd} onChange={e => setNewPwd(e.target.value)} required data-testid="new-pwd-input" /></div>
              <div><Label>Confirm New Password</Label><Input type="password" value={confirmPwd} onChange={e => setConfirmPwd(e.target.value)} required data-testid="confirm-pwd-input" /></div>
            </div>
            <div className="flex justify-end">
              <Button type="submit" className="careiq-btn-primary" disabled={pwdSaving} data-testid="change-password-btn">
                {pwdSaving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Lock className="w-4 h-4 mr-1" />}Change Password
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Related Lists — joined records (tickets, submissions, voice notes…) */}
      <RelatedLists scope="user" id="me" />

      {/* My Assigned Assets — equipment, devices, badges, etc. issued to me */}
      <Card className="careiq-card" data-testid="my-assets-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-semibold text-[#0A2342] flex items-center gap-2">
            <Package className="w-5 h-5 text-[#0E7490]" />My Assigned Assets
            {myAssets.length > 0 && (
              <span className="text-xs font-medium text-[#0E7490] bg-[#0E7490]/10 px-2 py-0.5 rounded-full" data-testid="my-assets-count">
                {myAssets.length}
              </span>
            )}
          </CardTitle>
          <Button size="sm" variant="outline" onClick={loadMyAssets} disabled={assetsLoading} data-testid="my-assets-refresh-btn">
            {assetsLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </Button>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-[#64748B] mb-3">Equipment, devices, and badges currently checked out to you. Contact your admin to return or report a lost item.</p>
          {assetsLoading && myAssets.length === 0 && (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 text-[#0E7490] animate-spin" /></div>
          )}
          {!assetsLoading && myAssets.length === 0 && (
            <div className="text-center py-8 text-sm text-[#94A3B8]" data-testid="my-assets-empty">
              No assets are currently assigned to you.
            </div>
          )}
          {myAssets.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-[#64748B] border-b border-[#E2E8F0]">
                    <th className="py-2 pr-3 font-medium">Asset Tag</th>
                    <th className="py-2 pr-3 font-medium">Name</th>
                    <th className="py-2 pr-3 font-medium">Category</th>
                    <th className="py-2 pr-3 font-medium">Serial / Model</th>
                    <th className="py-2 pr-3 font-medium">Assigned</th>
                    <th className="py-2 font-medium">Warranty</th>
                  </tr>
                </thead>
                <tbody>
                  {myAssets.map((a) => (
                    <tr key={a.id} className="border-b border-[#F1F5F9] hover:bg-[#F8FAFC]" data-testid={`my-asset-row-${a.id}`}>
                      <td className="py-2 pr-3 font-mono text-[#0A2342]">{a.asset_tag || '—'}</td>
                      <td className="py-2 pr-3 text-[#0A2342] font-medium">{a.name || '—'}</td>
                      <td className="py-2 pr-3 text-[#64748B]">
                        {a.category_parent_name ? `${a.category_parent_name} · ` : ''}{a.category_name || '—'}
                      </td>
                      <td className="py-2 pr-3 text-[#64748B]">
                        {a.serial_number ? <span className="font-mono">{a.serial_number}</span> : null}
                        {a.serial_number && a.model ? ' · ' : ''}
                        {a.model || (!a.serial_number ? '—' : '')}
                      </td>
                      <td className="py-2 pr-3 text-[#64748B] whitespace-nowrap">
                        {a.assigned_at ? new Date(a.assigned_at).toLocaleDateString() : '—'}
                      </td>
                      <td className="py-2 text-xs">
                        {a.warranty_expired ? (
                          <span className="px-1.5 py-0.5 rounded bg-[#991B1B]/10 text-[#991B1B] font-medium">Expired</span>
                        ) : a.warranty_expiring_soon ? (
                          <span className="px-1.5 py-0.5 rounded bg-[#CA8A04]/10 text-[#92400E] font-medium">{a.warranty_days_remaining}d left</span>
                        ) : a.warranty_expires_at ? (
                          <span className="text-[#64748B]">{new Date(a.warranty_expires_at).toLocaleDateString()}</span>
                        ) : (
                          <span className="text-[#94A3B8]">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Activity Log — HIPAA transparency ("was this you?") */}
      <Card className="careiq-card" data-testid="activity-log-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-semibold text-[#0A2342] flex items-center gap-2">
            <Activity className="w-5 h-5 text-[#0E7490]" />Activity Log
          </CardTitle>
          <Button size="sm" variant="outline" onClick={loadActivity} disabled={activityLoading} data-testid="activity-refresh-btn">
            {activityLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </Button>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-[#64748B] mb-3">Review recent access to your account. If anything looks wrong, change your password immediately and contact your admin.</p>
          {activity.length === 0 && !activityLoading && (
            <div className="text-center py-8 text-sm text-[#94A3B8]">No recent activity recorded.</div>
          )}
          {activity.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-[#64748B] border-b border-[#E2E8F0]">
                    <th className="py-2 pr-3 font-medium">When</th>
                    <th className="py-2 pr-3 font-medium">Action</th>
                    <th className="py-2 pr-3 font-medium">Resource</th>
                    <th className="py-2 pr-3 font-medium">IP Address</th>
                    <th className="py-2 font-medium">Device</th>
                  </tr>
                </thead>
                <tbody>
                  {activity.slice(0, 50).map((log) => {
                    const ua = (log.user_agent || '').toLowerCase();
                    let device = 'Unknown';
                    if (ua.includes('mobile') || ua.includes('android') || ua.includes('iphone')) device = 'Mobile';
                    else if (ua.includes('mac os')) device = 'Mac';
                    else if (ua.includes('windows')) device = 'Windows';
                    else if (ua.includes('linux')) device = 'Linux';
                    else if (ua.includes('curl')) device = 'API';
                    return (
                      <tr key={log.id} className="border-b border-[#F1F5F9] hover:bg-[#F8FAFC]" data-testid={`activity-row-${log.id}`}>
                        <td className="py-2 pr-3 text-[#0A2342] whitespace-nowrap">{log.timestamp ? new Date(log.timestamp).toLocaleString() : '—'}</td>
                        <td className="py-2 pr-3">
                          <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${
                            log.action === 'login' ? 'bg-[#166534]/10 text-[#166534]' :
                            log.action === 'logout' ? 'bg-[#64748B]/10 text-[#64748B]' :
                            log.action === 'delete' ? 'bg-[#991B1B]/10 text-[#991B1B]' :
                            log.action === 'update' || log.action === 'create' ? 'bg-[#1A56DB]/10 text-[#1A56DB]' :
                            'bg-[#0E7490]/10 text-[#0E7490]'
                          }`}>{log.action || '—'}</span>
                        </td>
                        <td className="py-2 pr-3 text-[#64748B]">{log.resource_type || '—'}{log.resource_id && log.resource_id !== 'multiple' ? ` · ${log.resource_id.slice(-6)}` : ''}</td>
                        <td className="py-2 pr-3 text-[#64748B] font-mono">{log.ip_address || '—'}</td>
                        <td className="py-2 text-[#64748B]">{device}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {activity.length > 50 && (
                <div className="text-center text-[10px] text-[#94A3B8] mt-2">Showing 50 of {activity.length} recent events</div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
